import Link from 'next/link';

// Problem → What we did → Results → Client quote, plus links to the services
// involved. Shared by the Our Work portfolio pages so each one shows outcomes,
// not just the visual identity.

export type CaseStudyResult = { num: string; label: string; source?: string };
export type CaseStudyQuote = { text: string; name: string; role: string };
export type CaseStudyService = { label: string; href: string };

type Theme = {
    ink: string;      // headings
    body: string;     // paragraph text
    accent: string;   // eyebrows
    panel: string;    // results/quote card background
    panelInk: string; // text on panel
    panelAccent: string; // result numbers on panel
};

type Props = {
    problem: string;
    approach: string[];
    results: CaseStudyResult[];
    quote?: CaseStudyQuote;
    services: CaseStudyService[];
    theme: Theme;
};

const display = 'var(--font-manrope), Manrope, sans-serif';

export default function CaseStudyStory({ problem, approach, results, quote, services, theme }: Props) {
    const eyebrow: React.CSSProperties = {
        fontFamily: display,
        fontWeight: 800,
        fontSize: 'clamp(11px, 0.9vw, 14px)',
        letterSpacing: '0.15em',
        textTransform: 'uppercase',
        color: theme.accent,
        margin: '0 0 14px',
    };
    const para: React.CSSProperties = {
        fontFamily: display,
        fontSize: 'clamp(14px, 1.1vw, 17px)',
        lineHeight: 1.8,
        letterSpacing: '0.03em',
        color: theme.body,
        margin: 0,
    };

    return (
        <section className="cs-story" style={{ margin: 'clamp(40px, 5vw, 72px) 0 0' }}>
            <style>{`
                .cs-story-cols { display: grid; grid-template-columns: 1fr 1fr; gap: clamp(32px, 5vw, 80px); }
                .cs-story-results { display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 16px; }
                .cs-story-service:hover { opacity: 0.75; }
                @media (max-width: 768px) {
                    .cs-story-cols { grid-template-columns: 1fr; }
                }
            `}</style>

            <div className="cs-story-cols">
                <div>
                    <h2 style={eyebrow}>The Problem</h2>
                    <p style={para}>{problem}</p>
                </div>
                <div>
                    <h2 style={eyebrow}>What We Did</h2>
                    <ul style={{ ...para, paddingLeft: 20, display: 'grid', gap: 8 }}>
                        {approach.map((item) => <li key={item}>{item}</li>)}
                    </ul>
                </div>
            </div>

            <div style={{ marginTop: 'clamp(32px, 4vw, 56px)' }}>
                <h2 style={eyebrow}>Results</h2>
                <div className="cs-story-results">
                    {results.map((r) => (
                        <div key={r.label} style={{ background: theme.panel, borderRadius: 16, padding: 'clamp(20px, 2vw, 28px)' }}>
                            <div style={{ fontFamily: display, fontWeight: 800, fontSize: 'clamp(28px, 3vw, 44px)', color: theme.panelAccent, lineHeight: 1, marginBottom: 10 }}>
                                {r.num}
                            </div>
                            <p style={{ ...para, color: theme.panelInk, fontSize: 'clamp(13px, 1vw, 15px)', lineHeight: 1.6 }}>{r.label}</p>
                            {r.source && (
                                <p style={{ ...para, color: theme.panelInk, opacity: 0.6, fontSize: 12, marginTop: 8 }}>Source: {r.source}</p>
                            )}
                        </div>
                    ))}
                </div>
            </div>

            {quote && (
                <figure style={{ margin: 'clamp(32px, 4vw, 56px) 0 0', background: theme.panel, borderRadius: 16, padding: 'clamp(24px, 3vw, 40px)' }}>
                    <blockquote style={{ ...para, color: theme.panelInk, fontSize: 'clamp(16px, 1.4vw, 22px)', fontStyle: 'italic', margin: 0 }}>
                        &ldquo;{quote.text}&rdquo;
                    </blockquote>
                    <figcaption style={{ ...para, color: theme.panelAccent, fontWeight: 700, marginTop: 16 }}>
                        {quote.name}, <span style={{ fontWeight: 400, color: theme.panelInk }}>{quote.role}</span>
                    </figcaption>
                </figure>
            )}

            <div style={{ marginTop: 'clamp(28px, 3vw, 44px)', display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10 }}>
                <span style={{ ...para, fontWeight: 700, color: theme.ink }}>Services used:</span>
                {services.map((s) => (
                    <Link
                        key={s.href}
                        href={s.href}
                        className="cs-story-service"
                        style={{ ...para, fontWeight: 600, color: theme.ink, border: `1px solid ${theme.ink}`, borderRadius: 999, padding: '6px 16px', textDecoration: 'none' }}
                    >
                        {s.label} →
                    </Link>
                ))}
            </div>
        </section>
    );
}
